import { defineStore } from 'pinia'
import type { IAccountData } from '@/typings/account'
import { inject, ref } from 'vue'
import { removeAccessToken } from '@/helpers/auth.ts'
import { useToast } from 'primevue'
import i18n from '@/i18n'
import { errorHandlerKey, servicesKey } from '@/config/keys.ts'
import { onApiErrorHandler } from '@/helpers/backendErrors.ts'
import { useSubAccountStore } from '@/stores/subAccount.ts'
import { useRouter } from 'vue-router'
import { routeNames } from '@/router/names.ts'
import { DocumentToAcceptError } from '@/models/DocumentToAcceptError.ts'
import {
    CHILD_PROTECT_DOCUMENT,
    CURRENT_TERMS_DOCUMENT,
} from '@/config/requiredDocumentsCodes.ts'

export const useAccountStore = defineStore('account', () => {
    const subAccountStore = useSubAccountStore()
    const toast = useToast()
    const router = useRouter()

    const services = inject(servicesKey)
    const errorHandler = inject(errorHandlerKey)

    const accountData = ref<IAccountData | null>(null)

    const clearAccountData = (): void => {
        accountData.value = null
    }

    const markCurrentTermsAcceptedForSubAccount = (
        subAccountId: string,
        acceptedAt: string = new Date().toISOString(),
    ): void => {
        const assignedSubAccount = accountData.value?.assignedSubaccounts?.find(
            (subAccount) => subAccount.subaccountId === subAccountId,
        )

        if (!assignedSubAccount) {
            return
        }

        assignedSubAccount.currentTermsAcceptedAt = acceptedAt

        const currentTerms = assignedSubAccount.terms.find((term) => term.isCurrent)

        if (currentTerms) {
            currentTerms.acceptedAt = acceptedAt
        }
    }

    const loadAccountData = async (): Promise<{
        isSuccess: boolean
        documentToAccept: typeof CHILD_PROTECT_DOCUMENT | null
        currentTermsNotAccepted: boolean
    }> => {
        try {
            const data = await services!.account.getMyAccount()

            if (!data) {
                throw new Error('[loadAccountData] Missing data from getMyAccount')
            }

            accountData.value = data

            return { isSuccess: true, documentToAccept: null, currentTermsNotAccepted: false }
        } catch (err) {
            if (
                err instanceof DocumentToAcceptError &&
                err.documentToAccept === CURRENT_TERMS_DOCUMENT
            ) {
                accountData.value = null

                return { isSuccess: false, documentToAccept: null, currentTermsNotAccepted: true }
            }

            onApiErrorHandler({
                err,
                toast,
                errorTitle: i18n.global.t('Error'),
                defaultErrorMessage: i18n.global.t(
                    'There was a problem retrieving your account details',
                ),
                action: 'loadAccountData',
                errorHandler,
            })
            accountData.value = null

            const requiredDoc =
                err instanceof DocumentToAcceptError &&
                err.documentToAccept === CHILD_PROTECT_DOCUMENT
                    ? err.documentToAccept
                    : null

            return {
                isSuccess: false,
                documentToAccept: requiredDoc,
                currentTermsNotAccepted: false,
            }
        }
    }

    const loadChildProtectAcceptedStatus = async (): Promise<{
        isSuccess: boolean
        childProtectAccepted: boolean
    }> => {
        try {
            const { childProtectAccepted } = await services!.account.getIsChildProtectAccepted()

            return { isSuccess: true, childProtectAccepted }
        } catch (err) {
            onApiErrorHandler({
                err,
                toast,
                errorTitle: i18n.global.t('Error'),
                defaultErrorMessage: i18n.global.t(
                    'There was a problem retrieving your account details',
                ),
                action: 'loadChildProtectAcceptedStatus',
                errorHandler,
            })

            return { isSuccess: false, childProtectAccepted: false }
        }
    }

    const logout = (silent: boolean = false): void => {
        removeAccessToken()
        clearAccountData()
        subAccountStore.clearSubAccountData()

        if (silent) {
            return
        }

        toast.add({
            summary: i18n.global.t('Correct logout'),
            severity: 'success',
            life: 4000,
        })
    }

    const onUnauthorizedResponse = async (): Promise<void> => {
        removeAccessToken()
        logout(true)

        toast.add({
            severity: 'info',
            summary: i18n.global.t('Your session has expired, please log in again'),
            life: 5000,
        })

        await router.push({
            name: routeNames.LOGIN_VIEW_NAME,
        })
    }

    return {
        accountData,
        clearAccountData,
        markCurrentTermsAcceptedForSubAccount,
        logout,
        loadAccountData,
        loadChildProtectAcceptedStatus,
        onUnauthorizedResponse,
    }
})
